import { Alert, Button, Form, Input } from "antd";
import { Eye, EyeOff, LockKeyhole, LogIn, ShieldCheck, UserRound } from "lucide-react";
import { useRef, useState } from "react";
import { BrandLogo } from "../../../shared/branding/BrandLogo";
import "./AdminLoginPage.css";

type LoginValues = {
  account: string;
  password: string;
};

export type AdminLoginPageProps = {
  notice?: string;
  onLogin: (account: string, password: string) => Promise<void>;
};

export function AdminLoginPage({ notice, onLogin }: AdminLoginPageProps) {
  const [form] = Form.useForm<LoginValues>();
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const submittingRef = useRef(false);

  const submit = async (values: LoginValues) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPending(true);
    setError("");
    try {
      await onLogin(values.account.trim(), values.password);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录失败，请稍后重试");
    } finally {
      submittingRef.current = false;
      setPending(false);
    }
  };

  return (
    <main className="admin-login">
      <section className="admin-login__brand-panel" aria-label="Yahshi 控制台">
        <div className="admin-login__brand">
          <BrandLogo className="admin-login__logo" alt="Yahshi" />
          <span>Yahshi 控制台</span>
        </div>
        <div className="admin-login__brand-copy">
          <span className="admin-login__eyebrow">CREATOR PLATFORM</span>
          <h1>Yahshi 创作者平台</h1>
          <p>统一管理应用、配置与服务。</p>
        </div>
        <div className="admin-login__environment"><span aria-hidden="true" />生产环境</div>
      </section>

      <section className="admin-login__form-panel" aria-labelledby="admin-login-title">
        <div className="admin-login__form-wrap">
          <div className="admin-login__mobile-brand">
            <BrandLogo className="admin-login__logo" alt="Yahshi" />
            <span>Yahshi 控制台</span>
          </div>
          <div className="admin-login__heading">
            <span className="admin-login__heading-icon"><ShieldCheck size={20} aria-hidden="true" /></span>
            <div>
              <h2 id="admin-login-title">管理员登录</h2>
              <p>使用已授权的管理员账号进入控制台</p>
            </div>
          </div>

          <div className="admin-login__messages" aria-live="polite">
            {notice ? <Alert type="info" showIcon title={notice} /> : null}
            {error ? <Alert role="alert" type="error" showIcon title={error} /> : null}
          </div>

          <Form<LoginValues>
            form={form}
            layout="vertical"
            onFinish={(values) => void submit(values)}
            onValuesChange={() => error && setError("")}
            requiredMark={false}
            size="large"
          >
            <Form.Item name="account" label="账号" rules={[{ required: true, whitespace: true, message: "请输入账号" }]}>
              <Input
                autoFocus
                autoCapitalize="none"
                autoComplete="username"
                disabled={pending}
                maxLength={128}
                placeholder="请输入管理员账号"
                prefix={<UserRound size={17} aria-hidden="true" />}
              />
            </Form.Item>
            <Form.Item name="password" label="密码" rules={[{ required: true, message: "请输入密码" }]}>
              <Input.Password
                autoComplete="current-password"
                disabled={pending}
                maxLength={256}
                placeholder="请输入登录密码"
                prefix={<LockKeyhole size={17} aria-hidden="true" />}
                iconRender={(visible) => visible ? <EyeOff size={17} /> : <Eye size={17} />}
              />
            </Form.Item>
            <Button
              className="admin-login__submit"
              block
              type="primary"
              htmlType="submit"
              disabled={pending}
              icon={<LogIn size={17} aria-hidden="true" />}
              loading={pending}
            >
              {pending ? "正在登录" : "登录"}
            </Button>
          </Form>
          <p className="admin-login__security"><LockKeyhole size={14} aria-hidden="true" />仅限授权管理员访问</p>
        </div>
      </section>
    </main>
  );
}
